import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { saveAs } from 'file-saver';

import { PageableTableComponent } from '../../shared/controls/pageable-table/pageable-table.component';

import { DialogService } from '../../shared/controls/message-dialog/message-dialog.service';
import { InitializerService } from '../../shared/services/initializer-service';
import { ToastService } from '../../shared/toast/toast.service';
import { ErrorService } from '../../shared/services/error-service';
import { AuthService } from '../../shared/services/auth.service';
import { LoadingService } from '../../shared/services/loading.service';
import { LoadingHelper } from '../../shared/services/loading-helper';

import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { AreaConstants } from '../../shared/constants/area.constants';
import { PermissionConstants } from '../../shared/constants/permission.constants';

import { PolicyGroupService, DownloadService, FilteredPolicyGroupItem, PagingData } from '../../../service/proxy';

@Component({
    templateUrl: './policy-group.component.html',
    styleUrls: ['./policy-group.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
        PageableTableComponent,
        HasPermissionDirective,
    ],

    providers: [
        PolicyGroupService,
        DownloadService,
    ]
})
export class PolicyGroupComponent implements OnInit {
    public showEdit: boolean = false;
    public showDelete: boolean = false;

    public search: string = '';
    public paging: PagingData = new PagingData();
    public policyGroups: FilteredPolicyGroupItem[] = [];

    constructor(
        public authService: AuthService,
        private router: Router,
        private policyGroupService: PolicyGroupService,
        private downloadService: DownloadService,
        private dialogService: DialogService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private initializerService: InitializerService,
        private loadingService: LoadingService)
        {
            this.paging = this.initializerService.createPagingData();
        }

    ngOnInit(): void {
        this.initializePermissions();
    }

    initializePermissions(): void {
        this.showEdit = this.authService
            .hasPermission(AreaConstants.PolicyGroup, PermissionConstants.Edit);

        this.showDelete = this.authService
            .hasPermission(AreaConstants.PolicyGroup, PermissionConstants.Delete);
    }

    refreshGridData(): void {
        this.paging = this.initializerService.createPagingData();
        this.getPolicyGroups(this.paging);
    }

    getPolicyGroups(pagingData: PagingData = new PagingData): void {
        const request$ = this.policyGroupService.getFilteredPolicyGroups(
            this.search, 
            pagingData.pageNo, 
            pagingData.pageSize, 
            pagingData.sortDirection, 
            pagingData.sortColumn
        );
        
        LoadingHelper.withLoading(request$, this.loadingService)
            .subscribe({
                next: (data) => {
                    this.paging = data.pagingData;
                    this.policyGroups = data.results || [];
                },
                error: (error) => {
                    if (error.status === 204) {
                        this.policyGroups = [];
                        this.paging = this.initializerService.createPagingData();
                    }
                    else {
                        this.errorService.handleError(error);
                    }
                }
            });
    }

    getColumnMap() {
        return {
            name: 'Policy Group',
        };
    }

    addPolicyGroup(): void {
        this.router.navigate([`/policy-group/0`]);
    }

    editPolicyGroup(policyGroupId: number): void {
        this.router.navigate([`/policy-group/${policyGroupId}`]);
    }

    deletePolicyGroup(policyGroupId: number): void {
        this.dialogService.show('Delete', 'Are you sure you want to delete this record?', 'Warning')
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    this.policyGroupService.deletePolicyGroup(policyGroupId)
                        .subscribe((result) => {
                            this.getPolicyGroups(this.paging);
                            this.toastService.show('Policy Group deleted successfully.', 'success');
                        });
                }
            }
        );
    }

    downloadPolicyGroups(pagingData: PagingData): void {
        this.downloadService.downloadPolicyGroups(this.search, pagingData.pageNo, pagingData.pageSize, pagingData.sortDirection, pagingData.sortColumn)
            .subscribe(response => {
                if (response.status === 200) {
                    saveAs(response.data, 'Policy_Groups.xlsx');
                }
                else {
                    alert('Download failed');
                }
            }
        );
    }
}
